import { useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import { listenerProfileIdentity } from '@/mocks/listenerProfileIdentity';

export default function OyenteProfile() {
  const [isFollowing, setIsFollowing] = useState(false);
  const { openModal: openDownloadModal } = useAppDownload();

  const profile = listenerProfileIdentity;

  const handleToggleFollow = () => {
    setIsFollowing(!isFollowing);
    Swal.fire({
      toast: true,
      position: 'top-end',
      title: isFollowing ? 'Dejaste de seguir este perfil' : 'Ahora seguís este perfil',
      icon: isFollowing ? 'info' : 'success',
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
      buttonsStyling: false,
    });
  };

  const handleShareProfile = async () => {
    const shareUrl = `${window.location.origin}/oyente`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Perfil de ${profile.name}`,
          text: `Mirá el perfil de ${profile.name} en RadioFrecuenciaWeb`,
          url: shareUrl,
        });
      } catch {
        // user cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        Swal.fire({
          toast: true,
          position: 'top-end',
          title: 'Link copiado al portapapeles',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
          customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
          buttonsStyling: false,
        });
      } catch {
        Swal.fire({
          title: 'No se pudo compartir',
          text: 'Intentá copiar la URL manualmente.',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#f59e0b',
          customClass: {
            popup: 'rounded-2xl',
            title: 'font-bold',
            confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
            htmlContainer: 'text-sm',
          },
          buttonsStyling: false,
        });
      }
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTimeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return formatDate(dateStr);
  };

  return (
    <div className="min-h-screen bg-background-100/50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-10">

          {/* Profile Header */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            {/* Cover area */}
            <div className="relative h-40 md:h-52 bg-gradient-to-r from-emerald-500/20 via-teal-500/15 to-amber-500/10 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/40"></div>
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <button
                  onClick={handleShareProfile}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-50/80 hover:bg-background-50 text-foreground-700 transition-all cursor-pointer backdrop-blur-sm"
                  title="Compartir perfil"
                >
                  <i className="ri-share-line"></i>
                </button>
                <button
                  onClick={openDownloadModal}
                  className="px-3 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
                >
                  <i className="ri-download-2-line mr-1"></i>
                  App RFW
                </button>
              </div>
            </div>

            {/* Profile info */}
            <div className="px-6 md:px-8 pb-6 md:pb-8 -mt-16 md:-mt-20 relative z-10">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6">
                {/* Photo */}
                <div className="relative shrink-0">
                  <img
                    src={profile.photo}
                    alt={profile.name}
                    className="w-28 h-28 md:w-32 md:h-32 rounded-2xl object-cover object-top border-4 border-background-50 shadow-sm"
                  />
                  <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-accent-500 rounded-full flex items-center justify-center border-2 border-background-50">
                    <i className="ri-check-line text-background-50 text-xs"></i>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0 pt-2 sm:pt-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                    <h1 className="text-xl md:text-2xl font-bold text-foreground-950">{profile.name}</h1>
                    <span className="text-sm text-foreground-500 font-medium">{profile.username}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500 mb-2">
                    <span className="flex items-center gap-1">
                      <i className="ri-map-pin-line text-foreground-400"></i>
                      {profile.city}, {profile.country}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line text-foreground-400"></i>
                      Miembro desde {formatDate(profile.joinDate)}
                    </span>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl">{profile.bio}</p>

                  {/* Level Bar */}
                  <div className="mt-4 bg-background-100 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <Link
                      to="/gamificacion"
                      className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-lg bg-accent-500 flex items-center justify-center">
                        <i className="ri-flashlight-line text-background-50 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-foreground-950">Nivel {profile.level.number}</div>
                        <div className="text-xs text-foreground-500">{profile.level.name}</div>
                      </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-foreground-500">Progreso al Nivel {profile.level.number + 1}</span>
                        <span className="text-xs font-bold text-foreground-700">{profile.level.progress}%</span>
                      </div>
                      <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent-500 rounded-full transition-all duration-700 ease-out"
                          style={{ width: `${profile.level.progress}%` }}
                        ></div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-foreground-400">{profile.level.name}</span>
                        <span className="text-[10px] text-foreground-400 flex items-center gap-1">
                          {profile.level.pointsToNext} pts para {profile.level.nextLevel}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={handleToggleFollow}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap shrink-0 ${
                        isFollowing
                          ? 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200'
                          : 'bg-secondary-500 hover:bg-secondary-600 text-background-50'
                      }`}
                    >
                      <i className={`${isFollowing ? 'ri-user-unfollow-line' : 'ri-user-follow-line'} mr-1.5`}></i>
                      {isFollowing ? 'Siguiendo' : 'Seguir'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-6">
            {[
              { label: 'Horas Escuchadas', value: profile.stats.totalListeningHours.toLocaleString(), icon: 'ri-headphone-line', color: 'bg-primary-500' },
              { label: 'Mensajes', value: profile.stats.messagesSent.toString(), icon: 'ri-message-2-line', color: 'bg-accent-500' },
              { label: 'Favoritas', value: profile.stats.radiosFavorited.toString(), icon: 'ri-heart-line', color: 'bg-rose-500' },
              { label: 'Siguiendo', value: profile.stats.radiosFollowed.toString(), icon: 'ri-user-follow-line', color: 'bg-sky-500', link: '/favoritos' },
              { label: 'Eventos', value: profile.stats.eventsAttended.toString(), icon: 'ri-calendar-check-line', color: 'bg-emerald-500' },
            ].map((stat) => {
              const StatContent = (
                <div className="bg-background-50 rounded-xl p-4 text-center">
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center ${stat.color}`}>
                    <i className={`${stat.icon} text-background-50 text-lg`}></i>
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-0.5">{stat.label}</div>
                </div>
              );
              return (stat as any).link ? (
                <Link key={stat.label} to={(stat as any).link} className="hover:opacity-80 transition-opacity cursor-pointer">
                  {StatContent}
                </Link>
              ) : (
                <div key={stat.label}>{StatContent}</div>
              );
            })}
          </div>

          {/* Badges Section */}
          <div className="bg-background-50 rounded-2xl p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-award-line text-accent-500"></i>
                Insignias
              </h3>
              <Link to="/insignias" className="text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1">
                Ver colección
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
              <Link to="/top-fans" className="text-xs text-amber-700 font-medium hover:text-amber-800 transition-colors cursor-pointer flex items-center gap-1 bg-amber-100 px-3 py-1 rounded-full">
                <i className="ri-star-line text-xs"></i>
                Top Fans
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {profile.badges.map((badge) => (
                <div key={badge.name} className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${badge.color}`}>
                    <i className={`${badge.icon} text-lg`}></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground-950">{badge.name}</div>
                    <div className="text-xs text-foreground-500 truncate">{badge.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Favorites Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Favorite Radios */}
            <div className="bg-background-50 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-radio-line text-primary-500"></i>
                Radios Favoritas
              </h3>
              <div className="space-y-3">
                {profile.favoriteRadios.map((radio) => (
                  <Link
                    key={radio.id}
                    to={`/radio/${radio.stationName.toLowerCase().replace(/\s+/g, '-')}`}
                    className="flex items-center gap-3 bg-background-100 rounded-xl p-3 hover:bg-background-200 transition-all cursor-pointer group"
                  >
                    <img
                      src={radio.logoUrl}
                      alt={radio.stationName}
                      className="w-11 h-11 rounded-xl object-cover object-top shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors truncate">
                        {radio.stationName}
                      </div>
                      <div className="text-xs text-foreground-500">{radio.frequency} · {radio.category}</div>
                    </div>
                    {radio.isLive && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[10px] font-bold shrink-0">
                        <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse"></span>
                        EN VIVO
                      </span>
                    )}
                  </Link>
                ))}
              </div>
              <Link
                to="/radios"
                className="mt-3 w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-600 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Ver todas las radios
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>

            {/* Favorite Programs */}
            <div className="bg-background-50 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-music-line text-accent-500"></i>
                Programas Favoritos
              </h3>
              <div className="space-y-3">
                {profile.favoritePrograms.map((program) => (
                  <div key={program.id} className="bg-background-100 rounded-xl p-3">
                    <div className="flex items-start justify-between mb-1">
                      <div className="text-sm font-semibold text-foreground-950">{program.programName}</div>
                      <span className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-[10px] font-bold shrink-0">
                        {program.category}
                      </span>
                    </div>
                    <div className="text-xs text-foreground-500 mb-1">{program.stationName}</div>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line text-foreground-400"></i>
                        {program.hostName}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line text-foreground-400"></i>
                        {program.schedule}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Favorite Hosts */}
            <div className="bg-background-50 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-mic-line text-secondary-500"></i>
                Locutores Favoritos
              </h3>
              <div className="space-y-3">
                {profile.favoriteHosts.map((host) => (
                  <div key={host.id} className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
                    <img
                      src={host.photoUrl}
                      alt={host.name}
                      className="w-11 h-11 rounded-xl object-cover object-top shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 truncate">{host.name}</div>
                      <div className="text-xs text-foreground-500">{host.role} · {host.stationName}</div>
                    </div>
                    {host.isLive && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[10px] font-bold shrink-0">
                        <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse"></span>
                        AL AIRE
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-3 flex justify-center">
            <Link
              to="/favoritos"
              className="px-5 py-2.5 bg-background-50 hover:bg-background-100 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap border border-background-200"
            >
              <i className="ri-heart-3-line mr-1.5"></i>
              Gestionar favoritos
              <i className="ri-arrow-right-line ml-1 text-xs"></i>
            </Link>
          </div>

          {/* Recent Activity */}
          <div className="bg-background-50 rounded-2xl p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-history-line text-foreground-600"></i>
                Actividad Reciente
              </h3>
              <Link to="/historial" className="text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1">
                Ver historial
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[13px] top-2 bottom-2 w-px bg-background-200"></div>
              <div className="space-y-4">
                {profile.recentActivity.map((activity, idx) => (
                  <div key={activity.id} className="flex gap-4 relative">
                    {/* Dot */}
                    <div className={`relative z-10 w-[27px] h-[27px] rounded-full flex items-center justify-center shrink-0 ${activity.color} border-2 border-background-50`}>
                      <i className={`${activity.icon} text-xs`}></i>
                    </div>
                    <div className="flex-1 min-w-0 bg-background-100 rounded-xl p-3">
                      <p className="text-sm text-foreground-800 leading-relaxed">{activity.description}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-xs text-foreground-400">{formatTimeAgo(activity.timestamp)}</span>
                        {activity.stationName && (
                          <span className="text-xs text-primary-600 font-medium">{activity.stationName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-10 bg-background-50 rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-accent-500 flex items-center justify-center">
              <i className="ri-user-star-line text-background-50 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-950 mb-2">¿Esta es tu cuenta?</h3>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              Personalizá tu experiencia, guardá tus radios favoritas y llevá tu nivel de oyente al máximo.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/register-radio"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Quiero mi propia radio
              </Link>
            </div>
          </div>

        </div>
      </main>

      <Footer />

      <div className="md:hidden h-20"></div>
    </div>
  );
}